import React from 'react';
import { getSensitiveUrlParam } from '#modules/utils/sensitiveUrlParams';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { getAppName } from '#app/containers/ThemeLoader';
import useLiquidNobleApi, {
  useActiveEntityIds,
} from '#modules/utils/useLiquidNobleApi';
import { DialogBody } from '#components/Elements/Dialog/Dialog';
import DefaultLayout from '#components/Layouts/DefaultLayout/DefaultLayout';
import { Field as FormField, Form } from 'react-final-form';
import { useRedirect, useLocationParts } from '#modules/hooks/navigation';
import { errorResponseNotify } from '#modules/utils/errors';
import Label from '#components/Elements/Form/Field/Label';
import Input from '#components/Elements/Form/Controls/Input';
import Button from '#components/Elements/Button/Button';
import {
  saveAuthToken,
  saveEntityId,
  saveProfileId,
  saveUnlockingKey,
} from '#modules/utils/jwt';
import {
  PasswordHints,
  passwordsMatch,
  passwordRequirementsMet,
} from '#components/Elements/PasswordHints/PasswordHints';
import {
  useValidators,
  fieldIsRequired,
  QUIET_VALIDATION_FAILURE,
} from '#modules/utils/validatorsLocalized';
import { EntityContext } from '#modules/context/EntityContext';

import styles from './LogIn.scss';

export default function PasswordResetPage() {
  const appName = getAppName();
  const intl = useIntl();
  const liquidNobleApi = useLiquidNobleApi();
  const { queryParts } = useLocationParts();
  const redirect = useRedirect();
  const [submitting, setSubmitting] = React.useState(false);
  const [mfaVerified, setMfaVerified] = React.useState(false);
  const { entityId } = useActiveEntityIds();
  const { setActiveLockboxId } = React.useContext(EntityContext);

  const username = queryParts?.username;
  const passwordChangeCode = getSensitiveUrlParam('code') ?? undefined;

  const initialValues = React.useMemo(
    () => ({
      username,
      recoveryKey: '',
      verificationCode: '',
    }),
    [username],
  );

  const validateRequired = useValidators(fieldIsRequired);

  const onVerifyMFACode = React.useCallback(
    async (values, form) => {
      try {
        const response = await liquidNobleApi.post(
          '/user/password/code/verify/',
          {
            username,
            mfa_code: values.verificationCode.replace(/\s/g, ''),
          },
        );
        const { data } = response;
        if (data?.status === 'success') {
          setMfaVerified(true);
        }
      } catch (error) {
        errorResponseNotify(error);
      }
    },
    [liquidNobleApi, setMfaVerified, username],
  );

  const onRequestEmailCode = React.useCallback(async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await liquidNobleApi.post(
        '/user/password/code/request/',
        {
          username,
        },
      );
      const { data } = response;
      if (data?.status === 'success') {
        toast.success(
          intl.formatMessage({
            defaultMessage: 'Verification code sent to your email',
          }),
        );
      }
    } catch (error) {
      errorResponseNotify(error);
    }

    setTimeout(() => {
      setSubmitting(false);
    }, 2000);
  }, [liquidNobleApi, username, setSubmitting, intl, submitting]);

  const onChangePassword = React.useCallback(
    async (values, form) => {
      try {
        const response = await liquidNobleApi.post('/user/password/change/', {
          password: values.password,
          code: passwordChangeCode,
          recovery_key: values.recoveryKey,
          entity_id: entityId,
        });
        const { data } = response;
        if (data?.status === 'success') {
          form.reset();
          toast.success(
            intl.formatMessage({
              defaultMessage: 'Password changed successfully',
            }),
          );
          if (data?.token) {
            saveAuthToken(data.token);
            saveEntityId(data?.entity_id);
            saveProfileId(data?.profile_id);
            saveUnlockingKey(data?.unlocking_key);
            setActiveLockboxId(data?.lockbox_id);
            redirect(`/dashboard`);
          } else {
            redirect(`/log-in`);
          }
        }
      } catch (error) {
        errorResponseNotify(error);
      }
    },
    [
      liquidNobleApi,
      redirect,
      entityId,
      passwordChangeCode,
      intl,
      setActiveLockboxId,
    ],
  );

  const onSubmit = React.useCallback(
    async (values, form) => {
      if (submitting) {
        return;
      }
      setSubmitting(true);

      if (passwordChangeCode) {
        onChangePassword(values, form);
      } else {
        onVerifyMFACode(values, form);
      }

      setTimeout(() => {
        setSubmitting(false);
      }, 2000);
    },
    [submitting, onChangePassword, onVerifyMFACode, passwordChangeCode],
  );

  return (
    <DefaultLayout
      showNavigation={false}
      enableServiceBanner
      allowModalBackgroundInteraction
      modalTest="login"
      forceModalContent={
        <>
          <DialogBody className={styles.loginBody}>
            <Form
              onSubmit={onSubmit}
              initialValues={initialValues}
              render={({ handleSubmit, ...formProps }) => (
                <>
                  <div
                    className={classNames(
                      styles.loginHeader,
                      styles.createAccountRow,
                    )}
                  >
                    {!!passwordChangeCode && (
                      <FormattedMessage defaultMessage="New password" />
                    )}

                    {!passwordChangeCode &&
                      (!mfaVerified ? (
                        <FormattedMessage defaultMessage="Confirmation code" />
                      ) : (
                        <FormattedMessage defaultMessage="Check your email" />
                      ))}
                  </div>
                  {!!passwordChangeCode && (
                    <div className={styles.headerSubtitle}>
                      <FormattedMessage defaultMessage="Please provide the following details to complete the password change process." />
                    </div>
                  )}
                  {!passwordChangeCode && (
                    <div className={styles.headerSubtitle}>
                      {!mfaVerified ? (
                        <FormattedMessage defaultMessage="Before you can change your password, we need to make sure it’s really you." />
                      ) : (
                        <>
                          <FormattedMessage defaultMessage="Thank you for confirming your password change request." />
                          <br />
                          <br />
                          <FormattedMessage defaultMessage="We have now emailed you with information on further steps required to complete this procedure and assign a new password to your account." />
                        </>
                      )}
                    </div>
                  )}
                  <div>
                    <form onSubmit={handleSubmit} className={styles.loginForm}>
                      {!!passwordChangeCode && (
                        <>
                          <FormField
                            name="recoveryKey"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <>
                                <Label>
                                  <FormattedMessage defaultMessage="Recovery Key" />
                                  <Input
                                    disableOnFocus
                                    type="password"
                                    placeholder={intl.formatMessage({
                                      defaultMessage:
                                        'Recovery key from the welcome email',
                                    })}
                                    size="medium"
                                    maxLength={128}
                                    color={
                                      (touched &&
                                        error &&
                                        error !== QUIET_VALIDATION_FAILURE) ||
                                      (submitError && !dirtySinceLastSubmit)
                                        ? 'danger'
                                        : null
                                    }
                                    {...input}
                                  />
                                </Label>
                                <span className={styles.inputHint}>
                                  <FormattedMessage
                                    defaultMessage={`When you signed up for your {app} account we sent you the recovery keys by email with a subject "IMPORTANT - Your {app} Recovery Keys". Please enter one of the provided recovery keys here.`}
                                    values={{ app: appName }}
                                  />
                                </span>
                              </>
                            )}
                          </FormField>
                          <FormField
                            name="password"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <Label>
                                <FormattedMessage defaultMessage="Password" />
                                <Input
                                  disableOnFocus
                                  type="password"
                                  placeholder={intl.formatMessage({
                                    defaultMessage: 'Password',
                                  })}
                                  size="medium"
                                  maxLength={128}
                                  color={
                                    (touched &&
                                      error &&
                                      error !== QUIET_VALIDATION_FAILURE) ||
                                    (submitError && !dirtySinceLastSubmit)
                                      ? 'danger'
                                      : null
                                  }
                                  {...input}
                                />
                              </Label>
                            )}
                          </FormField>
                          <FormField
                            name="password_confirm"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <Label>
                                <FormattedMessage defaultMessage="Confirm Password" />
                                <Input
                                  disableOnFocus
                                  type="password"
                                  placeholder={intl.formatMessage({
                                    defaultMessage: 'Password',
                                  })}
                                  size="medium"
                                  maxLength={128}
                                  color={
                                    (touched &&
                                      error &&
                                      error !== QUIET_VALIDATION_FAILURE) ||
                                    (submitError && !dirtySinceLastSubmit)
                                      ? 'danger'
                                      : null
                                  }
                                  {...input}
                                />
                              </Label>
                            )}
                          </FormField>
                          <PasswordHints formProps={formProps} />
                        </>
                      )}
                      {!mfaVerified && !passwordChangeCode && (
                        <>
                          <FormField
                            name="username"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <Label>
                                <FormattedMessage defaultMessage="Email address or handle" />
                                <Input
                                  disableOnFocus
                                  type="email"
                                  readOnly
                                  size="medium"
                                  maxLength={128}
                                  color={
                                    (touched &&
                                      error &&
                                      error !== QUIET_VALIDATION_FAILURE) ||
                                    (submitError && !dirtySinceLastSubmit)
                                      ? 'danger'
                                      : null
                                  }
                                  {...input}
                                />
                              </Label>
                            )}
                          </FormField>
                          <FormField
                            name="verificationCode"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <>
                                <Label>
                                  <FormattedMessage defaultMessage="Verification code" />
                                  <Input
                                    disableOnFocus
                                    disableAutoComplete
                                    type="text"
                                    size="medium"
                                    maxLength={128}
                                    color={
                                      (touched &&
                                        error &&
                                        error !== QUIET_VALIDATION_FAILURE) ||
                                      (submitError && !dirtySinceLastSubmit)
                                        ? 'danger'
                                        : null
                                    }
                                    {...input}
                                  />
                                </Label>
                                <span className={styles.inputHint}>
                                  <FormattedMessage
                                    defaultMessage={`If you have a MFA app linked to your account (e.g. Google Authenticator), please enter the code from that app. Otherwise, please request an email verification code. You cannot use an email code if you have an MFA app linked to your account.`}
                                  />
                                </span>
                              </>
                            )}
                          </FormField>
                        </>
                      )}
                      <div className={styles.actionButtons}>
                        {!!passwordChangeCode && (
                          <Button
                            submit
                            color="primary"
                            disabled={
                              submitting ||
                              !passwordsMatch(
                                formProps.values.password,
                                formProps.values.password_confirm,
                              ) ||
                              !passwordRequirementsMet(
                                formProps.values.password,
                              )
                            }
                          >
                            <FormattedMessage defaultMessage="Change password" />
                          </Button>
                        )}
                        {!mfaVerified && !passwordChangeCode && (
                          <>
                            <Button
                              submit
                              color="primary"
                              disabled={submitting}
                            >
                              <FormattedMessage defaultMessage="Submit" />
                            </Button>
                            <Button
                              submit
                              color="secondary"
                              disabled={submitting}
                              onClick={onRequestEmailCode}
                            >
                              <FormattedMessage defaultMessage="Request email code" />
                            </Button>
                          </>
                        )}
                        <Button
                          submit
                          color="secondary"
                          disabled={submitting}
                          onClick={() => {
                            redirect(`/log-in`);
                          }}
                        >
                          <FormattedMessage defaultMessage="Cancel" />
                        </Button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            />
            <div className={styles.loginFooter} />
          </DialogBody>
        </>
      }
    />
  );
}
